"use client";
import { Zap, useCurrentUser, useSplitTreatments } from "@zapier/client";
import { SearchZapsOrder } from "@zapier/bff";
import {
  Alert,
  AlertDescription,
  BetaDataTableProvider as TableProvider,
} from "@zapier/design-system";
import {
  AssetsSparkSurface,
  BulkActionsWrapper,
  PageSizeProvider,
  Pagination,
  TrashMovedPopover,
  usePageSize,
  useRouterParams,
} from "@zapier/shared";
import { AssetTypes } from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";

import { ZapTable } from "./ZapTable";
import { ZapsHeader } from "./ZapsHeader/ZapsHeader";
import { useZapsList } from "./ZapsList";
import { useAppsFromZapsList } from "./ZapsList/useAppsFromZapsList";
import styles from "./Zaps.module.css";

export function ZapsWithPageSize() {
  const t = useTranslations("ZapTable");
  const tConnectionBanner = useTranslations("zaps-connection-filter-banner");
  const tTrashMovedPopover = useTranslations("trash-moved-banner");
  const { isZapAndCanvasUnifiedTrashEnabled } = useSplitTreatments();
  const {
    virtual,
    search,
    status,
    shared,
    details,
    connection,
    connections,
    app,
    apps: appsFilterParameter,
  } = useRouterParams();

  const { size: pageSize } = usePageSize();
  const { isLoading: isUserLoading } = useCurrentUser();

  const {
    page,
    order,
    zaps,
    totalPages,
    totalResults,
    isLoading,
    isFetching,
    sortChange,
  } = useZapsList({ pageSize });

  const shouldHideHeader = false;

  const apps = useAppsFromZapsList({ zaps });

  const isPageLoading = isLoading || isFetching || isUserLoading;

  return (
    <BulkActionsWrapper
      assetType={AssetTypes.Zap}
      assetsForCopyToAnotherAccount={zaps}
    >
      {({ bulkSelectActions, onCopyToAnotherAccount }) => (
        <>
          {!shouldHideHeader && <ZapsHeader showSearch={!virtual} />}
          {isZapAndCanvasUnifiedTrashEnabled && (
            <TrashMovedPopover
              title={tTrashMovedPopover("title")}
              description={tTrashMovedPopover("description")}
              dismissedKey="zaps"
            />
          )}
          {(isPageLoading || zaps.length > 0) && (
            <AssetsSparkSurface
              surfaceId={process.env.NEXT_PUBLIC_SPARK_SURFACE_ID_ZAPS}
            />
          )}
          {(!!connection || !!connections) &&
            !app &&
            !appsFilterParameter &&
            !isPageLoading && (
              <div className={styles.connectionFilterBanner}>
                <Alert
                  variant="banner"
                  status="info"
                  title={tConnectionBanner("title")}
                >
                  <AlertDescription>
                    {tConnectionBanner("description")}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          <ZapTable
            allowsSorting={!virtual}
            ariaLabel={t("aria-label")}
            bulkSelectActions={bulkSelectActions}
            isLoading={isPageLoading}
            isSearch={!!search || !!status}
            onSortChange={sortChange}
            order={(order && (order as SearchZapsOrder)) || undefined}
            searchQuery={search}
            shared={shared || undefined}
            showCheckboxes={!!bulkSelectActions}
            virtualQuery={virtual || undefined}
            zaps={zaps as Zap[]}
            apps={apps}
            details={details || undefined}
            onCopyToAnotherAccount={onCopyToAnotherAccount}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalResults}
            isLoading={isLoading || isFetching || isUserLoading}
          />
        </>
      )}
    </BulkActionsWrapper>
  );
}

export function Zaps() {
  return (
    <TableProvider>
      <PageSizeProvider>
        <ZapsWithPageSize />
      </PageSizeProvider>
    </TableProvider>
  );
}
